import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AppLayout from '../layout/AppLayout';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { COLORS } from '../../utils/constants';
import { FileText, ArrowLeft } from 'lucide-react';
import dayjs from 'dayjs';

const C = COLORS;

const QuizDetail = ({ basePath }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchQuiz = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/quizzes/${id}`);
      setQuiz(res.data);
    } catch (err) {
      toast.error('Failed to load quiz details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchQuiz();
  }, [fetchQuiz]);

  let detectedBasePath = basePath;
  if (!detectedBasePath) {
    if (location.pathname.includes('/pal/practice')) detectedBasePath = '/pal/practice';
    else if (location.pathname.includes('/pal/tests')) detectedBasePath = '/pal/tests';
    else if (location.pathname.includes('/pal/learning')) detectedBasePath = '/pal/learning';
    else if (location.pathname.includes('/pal/homework')) detectedBasePath = '/pal/homework';
    else if (quiz?.quiz_type) detectedBasePath = `/pal/${quiz.quiz_type.toLowerCase()}`;
    else detectedBasePath = '/pal/practice';
  }

  if (loading) return <AppLayout title="Details"><div style={{ padding: 40, textAlign: 'center', color: C.g500 }}>Loading...</div></AppLayout>;
  if (!quiz) return <AppLayout title="Details"><div style={{ padding: 40, textAlign: 'center', color: C.danger }}>Quiz not found</div></AppLayout>;

  return (
    <AppLayout title={`${quiz.name || 'Details'}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button onClick={() => navigate(detectedBasePath)} style={{ padding: 8, background: '#fff', border: `1px solid ${C.g200}`, borderRadius: 8, cursor: 'pointer', color: C.g600 }}>
          <ArrowLeft size={18} />
        </button>
        <h2 style={{ margin: 0, fontSize: 20, color: C.g900 }}>{quiz.name}</h2>
        <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: quiz.status === 'active' ? '#ECFDF5' : '#FEF2F2', color: quiz.status === 'active' ? '#059669' : '#DC2626' }}>
          {quiz.status.toUpperCase()}
        </span>
        {quiz.quiz_type && (
          <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: '#EEF2FF', color: '#4F46E5' }}>
            {quiz.quiz_type}
          </span>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 32 }}>
        <div style={{ background: '#fff', padding: 20, borderRadius: 12, border: `1px solid ${C.g200}` }}>
          <p style={{ margin: '0 0 4px', fontSize: 12, color: C.g400, textTransform: 'uppercase', fontWeight: 600 }}>Date Range</p>
          <p style={{ margin: 0, fontSize: 14, color: C.g800, fontWeight: 500 }}>{dayjs(quiz.start_date).format('MMM D, YYYY')} - {dayjs(quiz.end_date).format('MMM D, YYYY')}</p>
        </div>
        {quiz.duration && (
          <div style={{ background: '#fff', padding: 20, borderRadius: 12, border: `1px solid ${C.g200}` }}>
            <p style={{ margin: '0 0 4px', fontSize: 12, color: C.g400, textTransform: 'uppercase', fontWeight: 600 }}>Duration</p>
            <p style={{ margin: 0, fontSize: 14, color: '#D97706', fontWeight: 600 }}>⏱ {quiz.duration} Minutes</p>
          </div>
        )}
        <div style={{ background: '#fff', padding: 20, borderRadius: 12, border: `1px solid ${C.g200}` }}>
          <p style={{ margin: '0 0 4px', fontSize: 12, color: C.g400, textTransform: 'uppercase', fontWeight: 600 }}>Type & Board</p>
          <p style={{ margin: 0, fontSize: 14, color: C.g800, fontWeight: 500 }}>{quiz.question_type} {quiz.board_name ? `(${quiz.board_name})` : ''}</p>
        </div>
        <div style={{ background: '#fff', padding: 20, borderRadius: 12, border: `1px solid ${C.g200}` }}>
          <p style={{ margin: '0 0 4px', fontSize: 12, color: C.g400, textTransform: 'uppercase', fontWeight: 600 }}>Hierarchy</p>
          <p style={{ margin: 0, fontSize: 14, color: C.g800, fontWeight: 500 }}>{quiz.class_name} &gt; {quiz.subject_name}</p>
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.g200}` }}>
        <div style={{ padding: 24, borderBottom: `1px solid ${C.g100}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, color: C.g900, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileText size={20} color={C.primary} /> Selected Questions ({quiz.questions?.length || 0})
          </h3>
          <button onClick={() => navigate(`${detectedBasePath}/edit/${quiz.id}`)} style={{ padding: '8px 16px', background: C.primaryGhost, color: C.primary, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Edit Questions
          </button>
        </div>

        <div style={{ padding: 24 }}>
          {quiz.questions && quiz.questions.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {quiz.questions.map((q, idx) => (
                <div key={q.id} style={{ padding: 20, border: `1px solid ${C.g200}`, borderRadius: 12, background: C.g50 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: C.g900 }}>Q{idx + 1}.</span>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: C.g500, background: '#fff', padding: '2px 8px', borderRadius: 12, border: `1px solid ${C.g200}` }}>Level: {q.level}</span>
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#1D4ED8', background: '#EFF6FF', padding: '2px 8px', borderRadius: 12 }}>{q.topic_name || '-'}</span>
                          <span style={{ fontSize: 11, fontWeight: 600, color: C.g600, background: '#fff', padding: '2px 8px', borderRadius: 12, border: `1px solid ${C.g200}` }}>{q.sub_topic_name || '-'}</span>
                        </div>
                      </div>
                      <p style={{ margin: '0 0 16px', fontSize: 15, color: C.g800, lineHeight: 1.5 }}>{q.question_text}</p>
                      
                      {(q.option_a || q.option_b || q.option_c || q.option_d) && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                          {q.option_a && <div style={{ padding: '8px 12px', background: q.correct_answer === 'A' ? '#ECFDF5' : '#fff', border: `1px solid ${q.correct_answer === 'A' ? '#34D399' : C.g200}`, borderRadius: 8, fontSize: 13, color: C.g700 }}><strong>A:</strong> {q.option_a}</div>}
                          {q.option_b && <div style={{ padding: '8px 12px', background: q.correct_answer === 'B' ? '#ECFDF5' : '#fff', border: `1px solid ${q.correct_answer === 'B' ? '#34D399' : C.g200}`, borderRadius: 8, fontSize: 13, color: C.g700 }}><strong>B:</strong> {q.option_b}</div>}
                          {q.option_c && <div style={{ padding: '8px 12px', background: q.correct_answer === 'C' ? '#ECFDF5' : '#fff', border: `1px solid ${q.correct_answer === 'C' ? '#34D399' : C.g200}`, borderRadius: 8, fontSize: 13, color: C.g700 }}><strong>C:</strong> {q.option_c}</div>}
                          {q.option_d && <div style={{ padding: '8px 12px', background: q.correct_answer === 'D' ? '#ECFDF5' : '#fff', border: `1px solid ${q.correct_answer === 'D' ? '#34D399' : C.g200}`, borderRadius: 8, fontSize: 13, color: C.g700 }}><strong>D:</strong> {q.option_d}</div>}
                        </div>
                      )}

                      {q.answer_desc && (
                        <div style={{ padding: 14, background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, fontSize: 13, color: '#92400E', lineHeight: 1.6 }}>
                          <strong style={{ display: 'block', marginBottom: 6 }}>Explanation / Answer:</strong>
                          <div dangerouslySetInnerHTML={{ __html: q.answer_desc }} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: C.g500 }}>
              <p style={{ margin: 0, fontSize: 15 }}>No questions added yet.</p>
              <button onClick={() => navigate(`${detectedBasePath}/edit/${quiz.id}`)} style={{ marginTop: 12, padding: '8px 16px', background: C.primary, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Select Questions
              </button>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default QuizDetail;
